import React, { useEffect, useState } from 'react';
import { ThemeProvider, useTheme } from './ThemeContext';
import './theme.css';
import supabase from './supabase'; // Import your Supabase client
import Header from './header';
import Card from './card';
import Intro from './Intro';
import LanguagesTools from './skills';
import Achievements from './achievements';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AlgothonPage from './algothon';

const AppContent = () => {
  const { isDarkMode } = useTheme();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('Projects')
        .select('*');

      if (error) {
        setError(error.message);
      } else {
        setProjects(data);
        setFilteredProjects(data);
      }
      setLoading(false);
    };

    fetchProjects();
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = projects.filter((project) =>
      project.tech_stack.toLowerCase().includes(query)
    );
    setFilteredProjects(filtered);
  };

  return (
    <div style={styles.app}>
      <Header />
      <Routes>
        <Route
          path="/"
          element={
            <main style={styles.main}>
              <Intro />
              <section style={styles.section}>
                <Achievements />
              </section>
              <section style={styles.section}>
                <div style={styles.sectionHeader}>
                  <h2 style={styles.sectionTitle}>Projects</h2>
                  <p style={styles.sectionSubtitle}>
                    (200+ Git Contributions in the last year)
                  </p>
                </div>
                <div style={styles.searchContainer}>
                  <input
                    type="text"
                    placeholder="Search by tech stack..."
                    value={searchQuery}
                    onChange={handleSearch}
                    style={styles.searchBar}
                  />
                </div>
                {loading && (
                  <div style={styles.loadingContainer}>
                    <div style={styles.loadingSpinner}></div>
                    <p style={styles.loadingText}>Loading projects...</p>
                  </div>
                )}
                {error && (
                  <div style={styles.errorContainer}>
                    <p style={styles.errorText}>
                      Error loading projects: {error}
                    </p>
                  </div>
                )}
                <div style={styles.grid}>
                  {filteredProjects.map((project) => (
                    <Card
                      key={project.id}
                      name={project.name}
                      shortDescription={project.short_description}
                      codeUrl={project.code_url}
                      techStack={project.tech_stack}
                      awards={project.Awards}
                      devpost={project.devpost_url}
                    />
                  ))}
                </div>
              </section>
              <section style={styles.section}>
                <LanguagesTools />
              </section>
            </main>
          }
        />
        <Route path="/algothon" element={<AlgothonPage />} />
      </Routes>
    </div>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
};

const styles = {
  app: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
  },
  main: {
    paddingTop: '4rem',
  },
  section: {
    padding: '2rem 1rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  sectionTitle: {
    fontSize: '2.5rem',
    color: 'var(--gray-900)',
    marginBottom: '0.5rem',
    fontWeight: '700',
    '@media (max-width: 768px)': {
      fontSize: '2rem',
    },
  },
  sectionSubtitle: {
    fontSize: '1.125rem',
    color: 'var(--gray-600)',
    fontStyle: 'italic',
    '@media (max-width: 768px)': {
      fontSize: '1rem',
    },
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '2rem',
    padding: '0 1rem',
  },
  searchBar: {
    padding: '1rem 1.5rem',
    fontSize: '1rem',
    borderRadius: '8px',
    border: '2px solid var(--gray-200)',
    width: '100%',
    maxWidth: '500px',
    transition: 'all 0.2s ease-in-out',
    backgroundColor: 'white',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
    padding: '0 1rem',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
      gap: '1.5rem',
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '2rem',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid var(--gray-200)',
    borderTop: '4px solid var(--primary-color)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '1rem',
  },
  loadingText: {
    color: 'var(--gray-600)',
    fontSize: '1.125rem',
  },
  errorContainer: {
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: 'var(--gray-100)',
    borderRadius: '8px',
    margin: '2rem 1rem',
  },
  errorText: {
    color: '#dc2626',
    fontSize: '1.125rem',
  },
};

export default App;
